import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Settings, 
  Plus, 
  Trash2, 
  Calculator,
  User,
  FileText,
  Save,
  X
} from "lucide-react";
import { Orcamento } from "@/hooks/useOrcamentos";
import type { Cliente } from "@/hooks/useClientes";
import { useEquipamentos } from "@/hooks/useEquipamentos";
import { usePlanosSeguro } from "@/hooks/usePlanosSeguro";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";
import { useAtualizarOrcamento } from "@/hooks/useOrcamentos";
import { isFormaPagamentoCartao, resolveFormaPagamentoId, numeroParcelasAoSelecionarForma } from "@/lib/orcamentoFormaPagamento";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface EditarOrcamentoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orcamento: Orcamento | null;
}

interface EquipamentoSelecionado {
  id: string;
  equipamento_id: string;
  quantidade: number;
  preco_unitario: number;
  marca: string;
  modelo: string;
  tipo: string;
  potencia?: string;
}

type DadosOriginaisOrcamento = {
  cliente_id?: number | null;
  valor_total?: number | null;
  quantidade_placas?: number | null;
  valor_seguro?: number | null;
};

export function EditarOrcamentoDialog({ open, onOpenChange, orcamento }: EditarOrcamentoDialogProps) {
  const queryClient = useQueryClient();
  const { data: equipamentos = [] } = useEquipamentos();
  const { data: planosSeguro = [] } = usePlanosSeguro();
  const { data: formasPagamento = [] } = useFormasPagamentoAtivas();
  const atualizarOrcamento = useAtualizarOrcamento();
  const dadosOriginaisRef = useRef<DadosOriginaisOrcamento | null>(null);

  const { data: orcamentoDb, isLoading: carregandoOrcamento } = useQuery({
    queryKey: ["orcamento-edit", orcamento?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orcamentos")
        .select(`
          *,
          cliente:clientes(id, nome, email, telefone, cpf_cnpj, endereco, numero, bairro, cidade, estado, cep),
          plano:planos_seguro(*)
        `)
        .eq("id", orcamento!.id)
        .single();
      if (error) throw error;
      return data as Orcamento;
    },
    enabled: open && !!orcamento?.id,
    staleTime: 0,
  });

  const orcamentoAtual = orcamentoDb ?? orcamento;

  const { data: clienteResolvido, isLoading: carregandoCliente } = useQuery({
    queryKey: [
      "orcamento-edit-cliente",
      orcamentoDb?.id,
      orcamentoDb?.cliente_id,
      orcamentoDb?.projeto_id,
    ],
    queryFn: async (): Promise<Cliente | Orcamento["cliente"] | null> => {
      const o = orcamentoDb!;
      if (o.cliente?.nome) return o.cliente;

      if (o.cliente_id) {
        const { data } = await supabase
          .from("clientes")
          .select("*")
          .eq("id", o.cliente_id)
          .maybeSingle();
        if (data) return data as Cliente;
      }

      if (o.projeto_id) {
        const { data: projeto } = await supabase
          .from("projetos")
          .select("cliente_id, cliente:clientes(*)")
          .eq("id", o.projeto_id)
          .maybeSingle();
        const projetoRow = projeto as unknown as {
          cliente?: Cliente | null;
          cliente_id?: number;
        };
        const embed = projetoRow.cliente;
        if (embed && !Array.isArray(embed) && embed.nome) return embed;
        const pid = projetoRow.cliente_id;
        if (pid) {
          const { data } = await supabase
            .from("clientes")
            .select("*")
            .eq("id", pid)
            .maybeSingle();
          if (data) return data as Cliente;
        }
      }

      return null;
    },
    enabled: open && !!orcamentoDb,
    staleTime: 0,
  });

  const clienteExibicao = clienteResolvido ?? orcamentoAtual?.cliente ?? null;

  const [formData, setFormData] = useState({
    cliente_id: '',
    valor_total: 0,
    observacoes: '',
    plano_id: '',
    vigencia_anos: 1,
    quantidade_placas: 0,
    forma_pagamento: '',
    parcelas_cartao: 1
  });



  const [equipamentosSelecionados, setEquipamentosSelecionados] = useState<EquipamentoSelecionado[]>([]);
  const [novoEquipamento, setNovoEquipamento] = useState({
    equipamento_id: '',
    quantidade: 1
  });

  // Carregar dados completos do orçamento ao abrir o modal
  useEffect(() => {
    if (!open || !orcamentoAtual) return;

    dadosOriginaisRef.current = {
      cliente_id: orcamentoAtual.cliente_id,
      valor_total: orcamentoAtual.valor_total,
      quantidade_placas: orcamentoAtual.quantidade_placas,
      valor_seguro: orcamentoAtual.valor_seguro,
    };

    setFormData({
      cliente_id: orcamentoAtual.cliente_id?.toString() || "",
      valor_total: Number(orcamentoAtual.valor_total) || 0,
      observacoes: orcamentoAtual.observacoes || "",
      plano_id: orcamentoAtual.plano_id?.toString() || "",
      vigencia_anos: orcamentoAtual.vigencia_anos || 1,
      quantidade_placas: Number(orcamentoAtual.quantidade_placas) || 0,
      forma_pagamento: resolveFormaPagamentoId(
        orcamentoAtual.forma_pagamento,
        formasPagamento
      ),
      parcelas_cartao:
        orcamentoAtual.numero_parcelas || orcamentoAtual.parcelas_cartao || 1,
    });

    setEquipamentosSelecionados([]);
    buscarEquipamentosOrcamento(orcamentoAtual.id.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recarregar só quando o id do orçamento mudar
  }, [open, orcamentoDb?.id, orcamento?.id]);

  // Normalizar forma de pagamento quando a lista de formas carregar
  useEffect(() => {
    if (!open || !orcamentoAtual || formasPagamento.length === 0) return;
    const resolved = resolveFormaPagamentoId(
      orcamentoAtual.forma_pagamento,
      formasPagamento
    );
    if (resolved) {
      setFormData((prev) =>
        prev.forma_pagamento === resolved
          ? prev
          : { ...prev, forma_pagamento: resolved }
      );
    }
  }, [open, orcamentoAtual?.id, formasPagamento, orcamentoDb]);

  const buscarEquipamentosOrcamento = async (orcamentoId: string) => {
    try {
      console.log('🔍 DEBUG - Buscando equipamentos do orçamento:', orcamentoId);
      
      // Primeiro, buscar equipamentos do orçamento na tabela orcamento_equipamentos
      const { data: equipamentosOrcamento, error: errorOrcamento } = await supabase
        .from('orcamento_equipamentos')
        .select(`
          *,
          equipamento:equipamentos(*)
        `)
        .eq('orcamento_id', orcamentoId);

      if (errorOrcamento) {
        console.error('❌ Erro ao buscar equipamentos do orçamento:', errorOrcamento);
      }

      console.log('🔍 DEBUG - Equipamentos do orçamento encontrados:', equipamentosOrcamento);

      // Se não encontrou equipamentos no orçamento, buscar no projeto
      if (!equipamentosOrcamento || equipamentosOrcamento.length === 0) {
        console.log('🔍 DEBUG - Nenhum equipamento no orçamento, buscando no projeto...');
        
        // Buscar o projeto relacionado ao orçamento
        const { data: projeto, error: errorProjeto } = await supabase
          .from('projetos')
          .select('id')
          .eq('orcamento_id', orcamentoId)
          .single();

        if (errorProjeto) {
          console.error('❌ Erro ao buscar projeto:', errorProjeto);
        } else if (projeto) {
          console.log('🔍 DEBUG - Projeto encontrado:', projeto);
          
          // Buscar equipamentos do projeto
          const { data: equipamentosProjeto, error: errorEquipamentosProjeto } = await supabase
            .from('projeto_equipamentos')
            .select(`
              *,
              equipamento:equipamentos(*)
            `)
            .eq('projeto_id', projeto.id);

          if (errorEquipamentosProjeto) {
            console.error('❌ Erro ao buscar equipamentos do projeto:', errorEquipamentosProjeto);
          } else {
            console.log('🔍 DEBUG - Equipamentos do projeto encontrados:', equipamentosProjeto);
            
            if (equipamentosProjeto && equipamentosProjeto.length > 0) {
              const equipamentosFormatados = equipamentosProjeto.map(eq => ({
                id: eq.id.toString(),
                equipamento_id: eq.equipamento_id?.toString() || '',
                quantidade: eq.quantidade || 1,
                preco_unitario: eq.preco_unitario || 0,
                marca: eq.equipamento?.marca || '',
                modelo: eq.equipamento?.modelo || '',
                tipo: eq.equipamento?.tipo || '',
                potencia: eq.equipamento?.potencia || ''
              }));

              setEquipamentosSelecionados(equipamentosFormatados);
              console.log('✅ Equipamentos do projeto carregados:', equipamentosFormatados);
              return;
            }
          }
        }
      } else {
        // Equipamentos encontrados no orçamento
        const equipamentosFormatados = equipamentosOrcamento.map(eq => ({
          id: eq.id.toString(),
          equipamento_id: eq.equipamento_id?.toString() || '',
          quantidade: eq.quantidade || 1,
          preco_unitario: eq.preco_unitario || 0,
          marca: eq.equipamento?.marca || '',
          modelo: eq.equipamento?.modelo || '',
          tipo: eq.equipamento?.tipo || '',
          potencia: eq.equipamento?.potencia || ''
        }));

        setEquipamentosSelecionados(equipamentosFormatados);
        console.log('✅ Equipamentos do orçamento carregados:', equipamentosFormatados);
        return;
      }

      // Se chegou aqui, não encontrou equipamentos em nenhum lugar
      console.log('🔍 DEBUG - Nenhum equipamento encontrado para este orçamento');
      setEquipamentosSelecionados([]);
    } catch (error) {
      console.error('💥 Erro ao buscar equipamentos do orçamento:', error);
      setEquipamentosSelecionados([]);
    }
  };

  const adicionarEquipamento = () => {
    if (!novoEquipamento.equipamento_id) {
      toast.error("Selecione um equipamento");
      return;
    }

    const equipamento = equipamentos.find(e => e.id.toString() === novoEquipamento.equipamento_id);
    if (!equipamento) {
      toast.error("Equipamento não encontrado");
      return;
    }

    const equipamentoSelecionado: EquipamentoSelecionado = {
      id: Date.now().toString(),
      equipamento_id: equipamento.id.toString(),
      quantidade: novoEquipamento.quantidade,
      preco_unitario: equipamento.preco_unitario,
      marca: equipamento.marca,
      modelo: equipamento.modelo,
      tipo: equipamento.tipo,
      potencia: equipamento.potencia
    };

    setEquipamentosSelecionados([...equipamentosSelecionados, equipamentoSelecionado]);
    setNovoEquipamento({ equipamento_id: '', quantidade: 1 });

    // Recalcular valor total
    const novoValorTotal = [...equipamentosSelecionados, equipamentoSelecionado]
      .reduce((total, eq) => total + (eq.preco_unitario * eq.quantidade), 0);
    setFormData(prev => ({ ...prev, valor_total: novoValorTotal }));
  };

  const removerEquipamento = (id: string) => {
    const equipamentosAtualizados = equipamentosSelecionados.filter(eq => eq.id !== id);
    setEquipamentosSelecionados(equipamentosAtualizados);

    // Recalcular valor total
    const novoValorTotal = equipamentosAtualizados
      .reduce((total, eq) => total + (eq.preco_unitario * eq.quantidade), 0);
    setFormData(prev => ({ ...prev, valor_total: novoValorTotal }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orcamentoAtual) return;

    const orig = dadosOriginaisRef.current;
    const valorTotal =
      formData.valor_total > 0
        ? formData.valor_total
        : Number(orig?.valor_total) || 0;
    const quantidadePlacas =
      formData.quantidade_placas > 0
        ? formData.quantidade_placas
        : Number(orig?.quantidade_placas) || 0;

    try {
      // Limpar completamente o texto automático das observações
      const observacoesLimpas = formData.observacoes?.replace(
        /Orçamento gerado do projeto \d+\. Plano: [^,]+.*$/s, 
        ''
      ).trim() || '';

      console.log('🔍 DEBUG - Dados a serem salvos:', {
        id: orcamentoAtual.id,
        valor_total: valorTotal,
        observacoes: observacoesLimpas, // Observações limpas
        plano_id: formData.plano_id || null,
        quantidade_placas: formData.quantidade_placas,
        vigencia_anos: formData.vigencia_anos,
        forma_pagamento: formData.forma_pagamento || null,
        parcelas_cartao: formData.parcelas_cartao
      });

      console.log('🔍 DEBUG - Equipamentos a serem salvos:', equipamentosSelecionados);

      // Parcelas: cartão = escolha do usuário; demais formas = parcelas_max do cadastro
      const parcelas = numeroParcelasAoSelecionarForma(
        formData.forma_pagamento,
        formasPagamento,
        formData.parcelas_cartao
      );

      const payload: Parameters<typeof atualizarOrcamento.mutateAsync>[0] = {
        id: orcamentoAtual.id,
        observacoes: observacoesLimpas,
        plano_id: formData.plano_id || null,
        vigencia_anos: formData.vigencia_anos,
        forma_pagamento: formData.forma_pagamento || null,
        parcelas_cartao: parcelas,
        numero_parcelas: parcelas,
      };
      if (valorTotal > 0) payload.valor_total = valorTotal;
      if (quantidadePlacas > 0) payload.quantidade_placas = quantidadePlacas;

      await atualizarOrcamento.mutateAsync(payload);

      const orcamentoId = Number(orcamentoAtual.id);

      // Salvar equipamentos na tabela orcamento_equipamentos
      let equipamentosOk = true;
      if (equipamentosSelecionados.length > 0) {
        console.log('🔍 DEBUG - Salvando equipamentos no banco...');

        const { error: deleteError } = await supabase
          .from('orcamento_equipamentos')
          .delete()
          .eq('orcamento_id', orcamentoId);

        if (deleteError) {
          console.error('❌ Erro ao remover equipamentos existentes:', deleteError);
          toast.error(`Erro ao limpar equipamentos antigos: ${deleteError.message}`);
          equipamentosOk = false;
        } else {
          const equipamentosParaSalvar = equipamentosSelecionados.map(eq => ({
            orcamento_id: orcamentoId,
            equipamento_id: parseInt(eq.equipamento_id, 10),
            quantidade: eq.quantidade,
            preco_unitario: eq.preco_unitario ?? 0,
          }));

          const { error: insertError } = await supabase
            .from('orcamento_equipamentos')
            .insert(equipamentosParaSalvar);

          if (insertError) {
            console.error('❌ Erro ao salvar equipamentos:', insertError);
            toast.error(`Orçamento salvo, mas equipamentos não atualizaram: ${insertError.message}`);
            equipamentosOk = false;
          } else {
            console.log('✅ Equipamentos salvos em orcamento_equipamentos');

            // Sincronizar projeto_equipamentos quando o orçamento tem projeto (dados únicos no orçamento/contrato)
            const projetoId = orcamentoAtual.projeto_id;
            if (projetoId) {
              const { error: delProjErr } = await supabase
                .from('projeto_equipamentos')
                .delete()
                .eq('projeto_id', Number(projetoId));

              if (delProjErr) {
                console.warn('⚠️ Erro ao atualizar equipamentos do projeto:', delProjErr);
              } else {
                const rowsProjeto = equipamentosParaSalvar.map((row) => ({
                  projeto_id: Number(projetoId),
                  orcamento_id: orcamentoId,
                  equipamento_id: row.equipamento_id,
                  quantidade: row.quantidade,
                  preco_unitario: row.preco_unitario,
                }));
                const { error: insertProjErr } = await supabase
                  .from('projeto_equipamentos')
                  .insert(rowsProjeto);
                if (insertProjErr) {
                  console.warn('⚠️ Erro ao sincronizar projeto_equipamentos:', insertProjErr);
                } else {
                  queryClient.invalidateQueries({ queryKey: ['projeto-equipamentos', Number(projetoId)] });
                  queryClient.invalidateQueries({ queryKey: ['projetos'] });
                }
              }
            }
          }
        }
      }

      if (!equipamentosOk) {
        toast.warning('Reabra o orçamento e tente salvar os equipamentos novamente.');
        return;
      }

      toast.success("Orçamento atualizado com sucesso!");
      onOpenChange(false);
    } catch (error) {
      console.error('💥 Erro ao atualizar orçamento:', error);
      toast.error("Erro ao atualizar orçamento");
    }
  };

  const getPlanoNome = (tipo: string) => {
    switch (tipo) {
      case 'basico': return 'Básico';
      case 'elevo': return 'Elevo';
      case 'renovacao-elevo': return 'Renovação Elevo';
      case 'personalizado': return 'Personalizado';
      default: return tipo;
    }
  };

  if (!orcamentoAtual) return null;

  const clienteLabel =
    clienteExibicao?.nome ||
    (orcamentoAtual.cliente_id
      ? `Cliente ID: ${orcamentoAtual.cliente_id}`
      : "Cliente não vinculado");
  const formaPagamentoSelectValue = formasPagamento.some(
    (f) => f.id.toString() === formData.forma_pagamento
  )
    ? formData.forma_pagamento
    : "";
  const planoSelectValue = planosSeguro.some(
    (p) => p.id.toString() === formData.plano_id
  )
    ? formData.plano_id
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Editar Orçamento - {orcamentoAtual.id}
          </DialogTitle>
        </DialogHeader>

        {carregandoOrcamento && (
          <p className="text-sm text-gray-500">Carregando dados do orçamento...</p>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs defaultValue="geral" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="geral">Geral</TabsTrigger>
              <TabsTrigger value="equipamentos">Equipamentos</TabsTrigger>
              <TabsTrigger value="cliente">Cliente</TabsTrigger>
              <TabsTrigger value="resumo">Resumo</TabsTrigger>
            </TabsList>

            <TabsContent value="geral" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="cliente_id">Cliente vinculado</Label>
                  <div className="p-3 bg-gray-50 rounded-lg border space-y-1">
                    {carregandoCliente && (
                      <p className="text-sm text-gray-500">Carregando cliente...</p>
                    )}
                    {!carregandoCliente && (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          {orcamentoAtual.cliente_id && (
                            <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded border">
                              ID {orcamentoAtual.cliente_id}
                            </span>
                          )}
                          <span className="font-medium text-gray-900">{clienteLabel}</span>
                        </div>
                        {clienteExibicao?.cpf_cnpj && (
                          <p className="text-sm text-gray-600">{clienteExibicao.cpf_cnpj}</p>
                        )}
                        {(clienteExibicao?.cidade || clienteExibicao?.estado) && (
                          <p className="text-sm text-gray-600">
                            {[clienteExibicao.estado, clienteExibicao.cidade].filter(Boolean).join(" / ")}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          O cliente do orçamento não pode ser alterado aqui. Para trocar, vincule outro cliente ao projeto.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="plano_id">Tipo de Plano</Label>
                  <Select
                    value={planoSelectValue}
                    onValueChange={(value) => setFormData({...formData, plano_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo de plano" />
                    </SelectTrigger>
                    <SelectContent>
                      {planosSeguro.map(plano => (
                        <SelectItem key={plano.id} value={plano.id.toString()}>
                          {plano.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                  <Select
                    value={formaPagamentoSelectValue}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        forma_pagamento: value,
                        parcelas_cartao: numeroParcelasAoSelecionarForma(
                          value,
                          formasPagamento,
                          formData.parcelas_cartao
                        ),
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a forma de pagamento">
                        {formData.forma_pagamento ? 
                          formasPagamento.find(f => f.id.toString() === formData.forma_pagamento)?.nome || 'Forma selecionada' 
                          : 'Selecione a forma de pagamento'
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map(forma => (
                        <SelectItem key={forma.id} value={forma.id.toString()}>
                          {forma.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isFormaPagamentoCartao(formData.forma_pagamento, formasPagamento) && (
                  <div>
                    <Label htmlFor="parcelas_cartao">Parcelas (Cartão)</Label>
                    <Input
                      id="parcelas_cartao"
                      type="number"
                      min="1"
                      max="12"
                      value={formData.parcelas_cartao}
                      onChange={(e) => setFormData({...formData, parcelas_cartao: parseInt(e.target.value)})}
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="vigencia_anos">Vigência (anos)</Label>
                  <Input
                    id="vigencia_anos"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.vigencia_anos}
                    onChange={(e) => setFormData({...formData, vigencia_anos: parseInt(e.target.value)})}
                  />
                </div>

                <div>
                  <Label htmlFor="quantidade_placas">Quantidade de Painéis</Label>
                  <Input
                    id="quantidade_placas"
                    type="number"
                    min="0"
                    value={formData.quantidade_placas}
                    onChange={(e) => setFormData({...formData, quantidade_placas: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="equipamentos" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Adicionar Equipamentos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="equipamento_id">Equipamento</Label>
                      <Select
                        value={novoEquipamento.equipamento_id}
                        onValueChange={(value) => setNovoEquipamento({...novoEquipamento, equipamento_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um equipamento" />
                        </SelectTrigger>
                        <SelectContent>
                          {equipamentos.map(equipamento => (
                            <SelectItem key={equipamento.id} value={equipamento.id.toString()}>
                              {equipamento.marca} {equipamento.modelo} - {equipamento.tipo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="quantidade">Quantidade</Label>
                      <Input
                        id="quantidade"
                        type="number"
                        min="1"
                        value={novoEquipamento.quantidade}
                        onChange={(e) => setNovoEquipamento({...novoEquipamento, quantidade: parseInt(e.target.value)})}
                      />
                    </div>

                    <div className="flex items-end">
                      <Button type="button" onClick={adicionarEquipamento} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Equipamentos Selecionados</CardTitle>
                </CardHeader>
                <CardContent>
                  {equipamentosSelecionados.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Nenhum equipamento selecionado</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {equipamentosSelecionados.map((equipamento) => (
                        <div key={equipamento.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium">{equipamento.marca} {equipamento.modelo}</div>
                            <div className="text-sm text-gray-600">
                              {equipamento.tipo} {equipamento.potencia && `- ${equipamento.potencia}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm text-gray-600">Qtd: {equipamento.quantidade}</div>
                              <div className="font-medium">
                                R$ {(equipamento.preco_unitario * equipamento.quantidade).toFixed(2).replace('.', ',')}
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removerEquipamento(equipamento.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cliente" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Informações do Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {clienteExibicao?.nome ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nome</Label>
                          <div className="p-2 bg-gray-50 rounded border font-medium">
                            {clienteExibicao.nome}
                          </div>
                        </div>
                        <div>
                          <Label>Email</Label>
                          <div className="p-2 bg-gray-50 rounded border">
                            {clienteExibicao.email || 'Não informado'}
                          </div>
                        </div>
                        <div>
                          <Label>Telefone</Label>
                          <div className="p-2 bg-gray-50 rounded border">
                            {clienteExibicao.telefone || 'Não informado'}
                          </div>
                        </div>
                        <div>
                          <Label>CPF/CNPJ</Label>
                          <div className="p-2 bg-gray-50 rounded border">
                            {clienteExibicao.cpf_cnpj || 'Não informado'}
                          </div>
                        </div>
                        {clienteExibicao.endereco && (
                          <div className="col-span-2">
                            <Label>Endereço</Label>
                            <div className="p-2 bg-gray-50 rounded border">
                              {clienteExibicao.endereco}
                              {clienteExibicao.cidade && `, ${clienteExibicao.cidade}`}
                              {clienteExibicao.estado && ` - ${clienteExibicao.estado}`}
                              {clienteExibicao.cep && `, CEP: ${clienteExibicao.cep}`}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Cliente não encontrado</p>
                      <p className="text-sm mt-2">ID do Cliente: {orcamentoAtual.cliente_id}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="resumo" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Resumo do Orçamento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>ID do Orçamento</Label>
                        <div className="p-2 bg-gray-50 rounded border font-mono">
                          {orcamentoAtual.id}
                        </div>
                      </div>
                      <div>
                        <Label>Status</Label>
                        <div className="p-2">
                          <Badge className={
                            orcamentoAtual.status === 'aprovado' ? 'bg-green-100 text-green-800' :
                            orcamentoAtual.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {orcamentoAtual.status.charAt(0).toUpperCase() + orcamentoAtual.status.slice(1)}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <Label>Tipo de Plano</Label>
                        <div className="p-2 bg-gray-50 rounded border">
                          {formData.plano_id ? planosSeguro.find(p => p.id === formData.plano_id)?.nome || 'Plano não especificado' : 'Plano não especificado'}
                        </div>
                      </div>
                      <div>
                        <Label>Vigência</Label>
                        <div className="p-2 bg-gray-50 rounded border">
                          {formData.vigencia_anos} ano{formData.vigencia_anos > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Valor Total</Label>
                      <div className="p-3 bg-blue-50 rounded border text-xl font-bold text-blue-900">
                        R$ {formData.valor_total.toFixed(2).replace('.', ',')}
                      </div>
                    </div>

                    <div>
                      <Label>Quantidade de Painéis</Label>
                      <div className="p-2 bg-gray-50 rounded border">
                        {formData.quantidade_placas || 0} painéis
                      </div>
                    </div>

                    <div>
                      <Label>Equipamentos ({equipamentosSelecionados.length})</Label>
                      <div className="p-2 bg-gray-50 rounded border">
                        {equipamentosSelecionados.length === 0 ? 'Nenhum equipamento selecionado' : 
                         `${equipamentosSelecionados.length} equipamento(s) selecionado(s)`}
                      </div>
                    </div>

                    <div>
                      <Label>Data de Criação</Label>
                      <div className="p-2 bg-gray-50 rounded border">
                        {new Date(orcamentoAtual.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>

                    {formData.observacoes && (
                      <div>
                        <Label>Observações</Label>
                        <div className="p-2 bg-gray-50 rounded border">
                          {formData.observacoes}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button type="submit" disabled={atualizarOrcamento.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {atualizarOrcamento.isPending ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 